import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Search, Headphones, History, ArrowDownUp, Pencil, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import adminApi from "@/lib/adminApi";

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("es", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`);
const fmtDate = (iso) => { if (!iso) return "—"; try { const hasTime = typeof iso === "string" && iso.length > 10; const d = new Date(hasTime ? iso : iso + "T00:00:00"); const b = `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`; return hasTime ? `${b} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` : b; } catch { return iso; } };
const MOV_LABEL = { ENTRADA: "Entrada", SALIDA: "Salida", RETIRO_CONFIRMADO: "Retiro confirmado", AJUSTE_POSITIVO: "Ajuste +", AJUSTE_NEGATIVO: "Ajuste −", REVERSO: "Reverso (anulación)" };

const readImage = (file, cb) => {
  if (!file) return;
  if (file.size > 1_500_000) { toast.error("Imagen muy grande (máx 1.5MB)"); return; }
  const r = new FileReader();
  r.onload = () => cb(r.result);
  r.readAsDataURL(file);
};

function CreateDialog({ owners, onClose, onCreated }) {
  const [form, setForm] = useState({ owner_id: "", name: "", category: "", sku: "", owner_cost_unit: "", sale_price_unit: "", initial_qty: "0", notes: "" });
  const [saving, setSaving] = useState(false);
  const selOwner = owners.find((o) => o.id === form.owner_id);
  const costLabel = selOwner?.settlement_mode === "COST_PLUS_PROFIT_SHARE" ? "Capital del consignador / unidad *" : "Precio del consignador / unidad *";
  const submit = async () => {
    setSaving(true);
    try {
      await adminApi.post("/admin/accessories", {
        owner_id: form.owner_id, name: form.name, category: form.category || null, sku: form.sku || null,
        owner_cost_unit: Number(form.owner_cost_unit || 0), sale_price_unit: form.sale_price_unit === "" ? null : Number(form.sale_price_unit),
        initial_qty: Number(form.initial_qty || 0), notes: form.notes || null,
      });
      toast.success("Accesorio creado"); onCreated(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const valid = form.owner_id && form.name.trim() && form.owner_cost_unit !== "";
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="acc-create-dialog">
        <DialogHeader><DialogTitle>Nuevo accesorio</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <div>
            <label className="text-xs font-medium text-slate-600">Dueño / Consignador *</label>
            <select data-testid="acc-owner" value={form.owner_id} onChange={(e) => setForm({ ...form, owner_id: e.target.value })} className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm mt-1">
              <option value="">Selecciona…</option>
              {owners.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.code})</option>)}
            </select>
          </div>
          <Input data-testid="acc-name" placeholder="Nombre (ej. Apple 20W USB-C Power Adapter)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl" />
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="acc-category" placeholder="Categoría" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-11 rounded-xl" />
            <Input data-testid="acc-sku" placeholder="SKU / referencia" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="h-11 rounded-xl" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs font-medium text-slate-600">{costLabel}</label><Input data-testid="acc-owner-cost" type="number" min="0" value={form.owner_cost_unit} onChange={(e) => setForm({ ...form, owner_cost_unit: e.target.value })} className="h-11 rounded-xl mt-1" /></div>
            <div><label className="text-xs font-medium text-slate-600">Precio venta PuntoCell/u</label><Input data-testid="acc-sale-price" type="number" min="0" value={form.sale_price_unit} onChange={(e) => setForm({ ...form, sale_price_unit: e.target.value })} className="h-11 rounded-xl mt-1" /></div>
          </div>
          <div><label className="text-xs font-medium text-slate-600">Cantidad</label><Input data-testid="acc-initial-qty" type="number" min="0" value={form.initial_qty} onChange={(e) => setForm({ ...form, initial_qty: e.target.value })} className="h-11 rounded-xl mt-1" /><p className="text-[11px] text-slate-400 mt-1">Unidades que estás recibiendo ahora.</p></div>
        </div>
        <Button data-testid="acc-create-submit" onClick={submit} disabled={saving || !valid} className="h-11 rounded-xl bg-slate-900 hover:bg-slate-800">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear accesorio"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ accessory, owners = [], onClose, onSaved }) {
  const _selOwner = owners.find((o) => o.id === accessory.owner_id);
  const costLabel = _selOwner?.settlement_mode === "COST_PLUS_PROFIT_SHARE" ? "Capital del consignador / unidad" : "Precio del consignador / unidad";
  const [form, setForm] = useState({
    name: accessory.name || "", category: accessory.category || "", sku: accessory.sku || "",
    owner_cost_unit: accessory.my_price ?? accessory.owner_cost_unit ?? "", sale_price_unit: accessory.sale_price_unit ?? "",
    active: accessory.active !== false, photo: accessory.photo || null,
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await adminApi.put(`/admin/accessories/${accessory.id}`, {
        name: form.name, category: form.category || null, sku: form.sku || null, photo: form.photo,
        owner_cost_unit: form.owner_cost_unit === "" ? null : Number(form.owner_cost_unit),
        sale_price_unit: form.sale_price_unit === "" ? null : Number(form.sale_price_unit), active: form.active,
      });
      toast.success("Accesorio actualizado"); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="acc-edit-dialog">
        <DialogHeader><DialogTitle>Editar · {accessory.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <div className="flex items-center gap-3">
            <div className="h-20 w-20 shrink-0 rounded-xl bg-slate-100 grid place-items-center overflow-hidden border border-slate-200" data-testid="acc-edit-photo-preview">
              {form.photo ? <img src={form.photo} alt="preview" className="h-full w-full object-cover" /> : <Headphones className="w-7 h-7 text-slate-300" />}
            </div>
            <div className="flex flex-col gap-2">
              <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-xs font-medium cursor-pointer hover:bg-slate-50" data-testid="acc-edit-photo-upload">
                <Upload className="w-3.5 h-3.5" /> {form.photo ? "Reemplazar foto" : "Subir foto"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => readImage(e.target.files?.[0], (d) => setForm((f) => ({ ...f, photo: d })))} />
              </label>
              {form.photo && <Button variant="outline" size="sm" data-testid="acc-edit-photo-remove" onClick={() => setForm((f) => ({ ...f, photo: null }))} className="h-9 rounded-lg text-xs text-rose-600 border-rose-200 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar foto</Button>}
            </div>
          </div>
          <Input data-testid="acc-edit-name" placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl" />
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="acc-edit-category" placeholder="Categoría" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-11 rounded-xl" />
            <Input data-testid="acc-edit-sku" placeholder="SKU / referencia" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="h-11 rounded-xl" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs font-medium text-slate-600">{costLabel}</label><Input data-testid="acc-edit-owner-cost" type="number" min="0" value={form.owner_cost_unit} onChange={(e) => setForm({ ...form, owner_cost_unit: e.target.value })} className="h-11 rounded-xl mt-1" /></div>
            <div><label className="text-xs font-medium text-slate-600">Precio venta PuntoCell/u</label><Input data-testid="acc-edit-sale-price" type="number" min="0" value={form.sale_price_unit} onChange={(e) => setForm({ ...form, sale_price_unit: e.target.value })} className="h-11 rounded-xl mt-1" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700" data-testid="acc-edit-active">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Producto activo
          </label>
          <p className="text-[11px] text-slate-400">La cantidad/stock NO se edita aquí; usa "Movimiento".</p>
        </div>
        <Button data-testid="acc-edit-submit" onClick={submit} disabled={saving || !form.name.trim()} className="h-11 rounded-xl bg-slate-900 hover:bg-slate-800">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar cambios"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function MovementDialog({ accessory, onClose, onDone }) {
  const [action, setAction] = useState("RECIBI");
  const [qty, setQty] = useState("1");
  const [target, setTarget] = useState(String(accessory.stock ?? 0));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const diff = Number(target) - Number(accessory.stock ?? 0);
  const needNotes = action === "SALIO" || (action === "CORREGIR" && diff < 0);
  const submit = async () => {
    let type, mqty;
    if (action === "RECIBI") { type = "ENTRADA"; mqty = Number(qty); }
    else if (action === "SALIO") { type = "SALIDA"; mqty = Number(qty); }
    else {
      if (Number.isNaN(diff) || diff === 0) { toast.error("La cantidad física debe ser distinta al stock actual"); return; }
      type = diff > 0 ? "AJUSTE_POSITIVO" : "AJUSTE_NEGATIVO"; mqty = Math.abs(diff);
    }
    if (!(mqty > 0)) { toast.error("Cantidad inválida"); return; }
    if (needNotes && !notes.trim()) { toast.error("Indica el motivo"); return; }
    setSaving(true);
    try { await adminApi.post(`/admin/accessories/${accessory.id}/movement`, { type, qty: mqty, notes: notes || null }); toast.success("Movimiento registrado"); onDone(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const OPTIONS = [
    ["RECIBI", "📦 Recibí mercancía", "Agregar unidades al inventario"],
    ["SALIO", "📤 Salió mercancía", "Registrar unidades que salieron del inventario"],
    ["CORREGIR", "🔧 Corregir cantidad", "Indica cuántas unidades hay realmente"],
  ];
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="acc-mov-dialog">
        <DialogHeader><DialogTitle>Movimiento · {accessory.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2" data-testid="acc-mov-stock">{accessory.reserved > 0 ? `Stock físico: ${accessory.stock} · Disponibles: ${accessory.available} · Reservadas: ${accessory.reserved}` : `Stock actual: ${accessory.stock} ${accessory.stock === 1 ? "unidad" : "unidades"}`}</p>
        <div className="grid gap-2">
          {OPTIONS.map(([val, title, help]) => (
            <button key={val} type="button" onClick={() => setAction(val)} data-testid={`acc-mov-opt-${val}`}
              className={`text-left rounded-xl border p-2.5 transition-colors ${action === val ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}>
              <span className="text-sm font-semibold text-slate-800">{title}</span>
              <p className="text-[11px] text-slate-500 mt-0.5">{help}</p>
            </button>
          ))}
        </div>
        {action === "CORREGIR" ? (
          <>
            <label className="text-xs font-medium text-slate-600">Cantidad física real</label>
            <Input data-testid="acc-mov-target" type="number" min="0" value={target} onChange={(e) => setTarget(e.target.value)} className="h-11 rounded-xl" placeholder="Unidades reales en existencia" />
            {!Number.isNaN(diff) && diff !== 0 && <p className="text-[11px] text-slate-400">Ajuste automático: {diff > 0 ? `+${diff}` : diff} (de {accessory.stock} a {target})</p>}
          </>
        ) : (
          <Input data-testid="acc-mov-qty" type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className="h-11 rounded-xl" placeholder="Cantidad" />
        )}
        <Input data-testid="acc-mov-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="h-11 rounded-xl" placeholder={`Motivo ${needNotes ? "*" : "(opcional)"}`} />
        <Button data-testid="acc-mov-submit" onClick={submit} disabled={saving} className="h-11 rounded-xl bg-slate-900 hover:bg-slate-800">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Registrar"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function MovementsView({ accessory, onClose }) {
  const [movs, setMovs] = useState(null);
  const [tab, setTab] = useState("movs");
  useEffect(() => { adminApi.get(`/admin/accessories/${accessory.id}/movements`).then((r) => setMovs(r.data)).catch(() => setMovs([])); }, [accessory.id]);
  const admin = accessory.admin_history || [];
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="acc-movs-dialog">
        <DialogHeader><DialogTitle>Historial · {accessory.name}</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          <button data-testid="acc-hist-tab-movs" onClick={() => setTab("movs")} className={`h-8 px-3 rounded-full text-xs font-medium ${tab === "movs" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>Movimientos físicos</button>
          <button data-testid="acc-hist-tab-admin" onClick={() => setTab("admin")} className={`h-8 px-3 rounded-full text-xs font-medium ${tab === "admin" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>Cambios de ficha</button>
        </div>
        {tab === "movs" ? (
          movs === null ? <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /> : movs.length === 0 ? <p className="text-sm text-slate-400 text-center py-6">Sin movimientos.</p> : (
            <div className="space-y-2 max-h-[55vh] overflow-y-auto">
              {movs.map((m) => (
                <div key={m.id} className={`flex items-center justify-between text-sm border-b border-slate-100 pb-2 ${m.voided ? "opacity-50" : ""}`}>
                  <div><p className="font-medium text-slate-800">{MOV_LABEL[m.type] || m.type}{m.reference ? <span className="text-slate-400 font-normal"> · {m.reference}</span> : ""}</p><p className="text-xs text-slate-400">{fmtDate(m.at)} · {m.by}{m.notes ? ` · ${m.notes}` : ""}</p></div>
                  <span className={`font-bold ${["ENTRADA", "AJUSTE_POSITIVO", "REVERSO"].includes(m.type) ? "text-emerald-600" : "text-rose-600"}`}>{["ENTRADA", "AJUSTE_POSITIVO", "REVERSO"].includes(m.type) ? "+" : "−"}{m.qty}</span>
                </div>
              ))}
            </div>
          )
        ) : (
          admin.length === 0 ? <p className="text-sm text-slate-400 text-center py-6">Sin cambios de ficha.</p> : (
            <div className="space-y-2 max-h-[55vh] overflow-y-auto" data-testid="acc-admin-history">
              {admin.slice().reverse().map((h, i) => (
                <div key={i} className="text-sm border-b border-slate-100 pb-2">
                  <p className="font-medium text-slate-800">{h.field}: <span className="text-slate-500 font-normal">{String(h.old ?? "—")} → {String(h.new ?? "—")}</span></p>
                  <p className="text-xs text-slate-400">{fmtDate(h.at)} · {h.by}</p>
                </div>
              ))}
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AccessoriesTab({ isAdmin, canManage }) {
  const [rows, setRows] = useState(null);
  const [owners, setOwners] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState("TODOS");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [moving, setMoving] = useState(null);
  const [viewing, setViewing] = useState(null);
  const load = () => { setRows(null); adminApi.get(`/admin/accessories?owner_id=${ownerFilter}`).then((r) => setRows(r.data)).catch(() => setRows([])); };
  useEffect(() => { adminApi.get("/admin/owners").then((r) => setOwners(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ownerFilter]);
  const term = q.trim().toLowerCase();
  const visible = useMemo(() => (rows || []).filter((a) => !term || [a.name, a.sku, a.category, a.owner_name].some((f) => f && String(f).toLowerCase().includes(term))), [rows, term]);

  return (
    <div data-testid="accessories-panel">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select data-testid="acc-owner-filter" value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} className="h-10 rounded-xl border border-slate-200 px-3 text-sm">
          <option value="TODOS">Todos los dueños</option>
          {owners.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.code})</option>)}
        </select>
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="acc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar producto / SKU..." className="h-10 rounded-xl pl-9" />
        </div>
        {canManage && <Button data-testid="acc-new-btn" onClick={() => setCreating(true)} className="h-10 rounded-xl bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1" /> Nuevo</Button>}
      </div>
      {rows === null ? <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> : visible.length === 0 ? (
        <p className="text-sm text-slate-400 py-10 text-center" data-testid="acc-empty">No hay accesorios.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {visible.map((a) => (
            <div key={a.id} data-testid={`acc-row-${a.id}`} className={`rounded-2xl border border-slate-200 bg-white p-4 ${a.active === false ? "opacity-60" : ""}`}>
              <div className="flex gap-3">
                <div className="h-14 w-14 shrink-0 rounded-xl bg-slate-100 grid place-items-center overflow-hidden">
                  {a.photo ? <img src={a.photo} alt={a.name} className="h-full w-full object-cover" data-testid={`acc-photo-${a.id}`} /> : <Headphones className="w-6 h-6 text-slate-400" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900 truncate">{a.name}{a.active === false ? <span className="text-[10px] font-normal text-rose-500"> · inactivo</span> : ""}</p>
                  <p className="text-xs text-slate-500">{[a.category, a.sku].filter(Boolean).join(" · ") || "—"} · {a.owner_name} ({a.owner_code})</p>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-slate-50 py-1.5"><p className="text-[10px] text-slate-400 uppercase">Disp.</p><p className="font-bold text-emerald-700" data-testid={`acc-available-${a.id}`}>{a.available}</p></div>
                <div className="rounded-lg bg-slate-50 py-1.5"><p className="text-[10px] text-slate-400 uppercase">Reserv.</p><p className="font-bold text-amber-600">{a.reserved}</p></div>
                <div className="rounded-lg bg-slate-50 py-1.5"><p className="text-[10px] text-slate-400 uppercase">Mi precio</p><p className="font-bold text-slate-900">{money(a.my_price)}</p></div>
              </div>
              {isAdmin && a.sale_price_unit != null && <p className="text-[11px] text-slate-400 mt-1.5">Venta PuntoCell: {money(a.sale_price_unit)}/u</p>}
              <div className="mt-3 flex flex-wrap gap-2">
                {canManage && <Button variant="outline" size="sm" data-testid={`acc-edit-${a.id}`} onClick={() => setEditing(a)} className="h-8 rounded-lg text-xs flex-1"><Pencil className="w-3 h-3 mr-1" /> Editar</Button>}
                {canManage && <Button variant="outline" size="sm" data-testid={`acc-move-${a.id}`} onClick={() => setMoving(a)} className="h-8 rounded-lg text-xs flex-1"><ArrowDownUp className="w-3 h-3 mr-1" /> Movimiento</Button>}
                <Button variant="outline" size="sm" data-testid={`acc-history-${a.id}`} onClick={() => setViewing(a)} className="h-8 rounded-lg text-xs flex-1"><History className="w-3 h-3 mr-1" /> Historial</Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {creating && <CreateDialog owners={owners} onClose={() => setCreating(false)} onCreated={load} />}
      {editing && <EditDialog accessory={editing} owners={owners} onClose={() => setEditing(null)} onSaved={load} />}
      {moving && <MovementDialog accessory={moving} onClose={() => setMoving(null)} onDone={load} />}
      {viewing && <MovementsView accessory={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
